import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PercentageOfPercentageCalculator = () => {
  const [inputData, setInputData] = useState({
    firstPercentage: '',
    secondPercentage: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculatePercentage = () => {
    const { firstPercentage, secondPercentage } = inputData;
    
    // Validate inputs
    if (!firstPercentage || !secondPercentage) {
      setError('Please enter both percentages');
      return;
    }

    const firstNum = parseFloat(firstPercentage);
    const secondNum = parseFloat(secondPercentage);

    if (isNaN(firstNum) || isNaN(secondNum)) {
      setError('Please enter valid numbers');
      return;
    }

    if (firstNum < 0 || firstNum > 100 || secondNum < 0 || secondNum > 100) {
      setError('Percentages must be between 0 and 100');
      return;
    }

    // Calculate result
    const result = (firstNum * secondNum) / 100;
    const formattedResult = result.toFixed(2);

    // Create visualization data
    const chartData = {
      labels: ['First Percentage', 'Second Percentage', 'Result'],
      datasets: [{
        label: 'Percentage Values',
        data: [firstNum, secondNum, result],
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 99, 132, 0.5)',
          'rgba(75, 192, 192, 0.5)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1
      }]
    };

    // Create calculation steps
    const calculationSteps = [
      `Step 1: Convert percentages to decimals`,
      `${firstNum}% → ${firstNum/100}`,
      `${secondNum}% → ${secondNum/100}`,
      '\nStep 2: Multiply the decimals',
      `${firstNum/100} × ${secondNum/100} = ${(firstNum/100)*(secondNum/100)}`,
      '\nStep 3: Convert back to percentage',
      `${(firstNum/100)*(secondNum/100)} × 100 = ${formattedResult}%`
    ];

    setResults({
      firstPercentage: firstNum,
      secondPercentage: secondNum,
      result: formattedResult,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    firstPercentage: 20,
    secondPercentage: 30,
    result: 6
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-green-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Percentage of a Percentage Calculator | Nested Percentage Calculator</title>
          <meta name="description" content="Calculate the percentage of a percentage. This calculator helps determine the result of applying one percentage to another, useful for financial calculations and statistical analysis." />
          <meta name="keywords" content="percentage calculator, nested percentage, financial calculator, percentage of percentage, math calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-blue-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Percentage of a Percentage Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate what percentage a value is of another percentage. This calculator helps determine the result of applying one percentage to another, useful for financial calculations and statistical analysis.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">First Percentage:</label>
              <input
                type="number"
                name="firstPercentage"
                value={inputData.firstPercentage}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter first percentage (e.g., 20)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Second Percentage:</label>
              <input
                type="number"
                name="secondPercentage"
                value={inputData.secondPercentage}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter second percentage (e.g., 30)"
              />
            </div>
          </div>

          <button
            onClick={calculatePercentage}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Summary</h3>
                  <p className="text-lg">{results.firstPercentage}% of {results.secondPercentage}%</p>
                  <p className="text-2xl font-bold text-blue-600 mt-2">{results.result}%</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Formula: (A% × B%) = (A × B) ÷ 100</p>
                  <p className="text-lg mt-2">Calculation: ({results.firstPercentage} × {results.secondPercentage}) ÷ 100 = {results.result}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Percentage Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Components'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Percentage Breakdown'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>First Percentage: {exampleData.firstPercentage}%</li>
                <li>Second Percentage: {exampleData.secondPercentage}%</li>
                <li>Result: {exampleData.result}%</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Convert percentages to decimals:
                  <ul className="list-disc pl-6">
                    <li>20% → 0.20</li>
                    <li>30% → 0.30</li>
                  </ul>
                </li>
                <li>Multiply the decimals:
                  <ul className="list-disc pl-6">
                    <li>0.20 × 0.30 = 0.06</li>
                  </ul>
                </li>
                <li>Convert back to percentage:
                  <ul className="list-disc pl-6">
                    <li>0.06 × 100 = 6%</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Percentage of a Percentage Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter First Percentage:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the first percentage value</li>
                  <li>Must be between 0 and 100</li>
                </ul>
              </li>
              <li><strong>Enter Second Percentage:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the second percentage value</li>
                  <li>Must be between 0 and 100</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The result of applying the first percentage to the second</li>
                  <li>A visual representation of the calculation</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use for financial calculations like compound interest</li>
                <li>Helpful for statistical analysis of nested percentages</li>
                <li>Result represents the intersection of two percentages</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Nested Percentages <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              A percentage of a percentage calculation determines what portion one percentage represents of another. This is useful in:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Financial Analysis:</strong> Calculating compound interest or fee reductions</li>
              <li><strong>Statistics:</strong> Analyzing overlapping probabilities</li>
              <li><strong>Scientific Research:</strong> Determining relative proportions</li>
              <li><strong>Business Metrics:</strong> Measuring nested KPIs</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Context Matters:</strong> Always consider the real-world context of your calculation</li>
              <li><strong>Decimal Precision:</strong> Use appropriate decimal places based on your needs</li>
              <li><strong>Double-check Inputs:</strong> Verify percentage values before calculating</li>
              <li><strong>Understand Units:</strong> Ensure all percentages are in the same unit of measurement</li>
            </ul>
            <p className="italic mt-4">
              Note: When dealing with financial calculations, consult with a professional for critical decisions.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PercentageOfPercentageCalculator;